// @ts-strict-ignore
import sum from 'lodash/sum';

import {Translatable} from 'common/i18n';
import {abbreviateNumber, capitalize, commaizeNumber} from 'common/util';
import {pluralize} from 'common/util/string';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {SPECIAL_EVENTS} from 'common/report/constants';
import {formatPercent as formatPercentFromCommon} from 'common/util';
import {customEventsStore} from 'common/report/stores/custom-events';
import {renameEvent} from 'common/report/util/events';
import {RetentionType} from 'common/types/reports/bookmark';

import {BirthIntervalUnit, RetentionBookmarkParams} from './types';

const UNIT = {
  DAY: `day`,
  WEEK: `week`,
  MONTH: `month`,
};
const SMALLER_UNIT_MAP = {
  [UNIT.DAY]: `hour`,
  [UNIT.WEEK]: `day`,
  [UNIT.MONTH]: `week`,
};

function getUnitSmaller(unit): string {
  return SMALLER_UNIT_MAP[unit];
}

export function getAddictionUnit(unit): string {
  // Simply becuase that is how addiction works
  return unit === UNIT.MONTH ? UNIT.DAY : getUnitSmaller(unit);
}

export function isAddiction(bookmarkParams: RetentionBookmarkParams) {
  return bookmarkParams.retention_type === `addiction`;
}

/**
 * Convert a decimal percentage to a formatted string percent to a certain precision
 * @example
 * formatPercent(0.2548, 1);
 * // 25.5%
 * formatPercent(12.342, 0);
 * // 1,234%
 **/

export function formatPercent(value: number, precision = 2): string {
  if (isNaN(value)) {
    value = 0;
  }

  return formatPercentFromCommon(value, {precision, exactPrecision: false, showSign: false});
}

/**
 * format a number to be ready to use in retention table
 * @param value number to be formatted
 * @returns {number}
 */
export function formatNumber(value: number): string {
  // In order to get an accurate count, round to 2 decimal places if the value is too small
  // Otherwise, round to the nearest integer
  const roundedValue = value < 5 ? Math.round(value * 100) / 100 : Math.round(value);
  return roundedValue < 1000000 ? commaizeNumber(roundedValue) : abbreviateNumber(roundedValue);
}

/**
 * Convert to number order given a number
 * @param number
 */

export function getRetentionBucketName({
  bucketIndex,
  retentionType,
  retentionUnit,
  dateUnit,
  bucketSizes,
}: {
  bucketIndex: number;
  retentionType: RetentionType;
  retentionUnit: TimeUnit;
  dateUnit: BirthIntervalUnit;
  bucketSizes: number[];
}): Translatable {
  return retentionType === `addiction` || !bucketSizes?.length
    ? getRetentionNonCustomBucketName({
        bucketIndex,
        retentionType,
        retentionUnit: retentionUnit || TimeUnit.Day,
        dateUnit: dateUnit || TimeUnit.Day,
      })
    : getRetentionCustomBucketName({bucketIndex, bucketSizes, retentionUnit: retentionUnit || TimeUnit.Day});
}

export function getRetentionNonCustomBucketName({
  bucketIndex,
  retentionType,
  retentionUnit,
  dateUnit,
}: {
  bucketIndex: number;
  retentionType: RetentionType;
  retentionUnit: TimeUnit;
  dateUnit: BirthIntervalUnit;
}): Translatable {
  // todo: i18n typesafety: TimeUnit is a bigger type than is supported in time-unit dictionary
  return retentionType === `addiction`
    ? // todo: i18n typesafety: getAddictionUnit needs to be typed
      ({t}) => `${bucketIndex + 2} ${t(`reports.time-unit.${getAddictionUnit(dateUnit)}` as any)}`
    : bucketIndex > 0
      ? ({t}) => `${t(`reports.time-unit.${retentionUnit}` as any)} ${bucketIndex}`
      : ({t}) => `< 1 ${t(`reports.time-unit.${retentionUnit}` as any)}`;
}

export function getRetentionCustomBucketName({
  bucketIndex,
  bucketSizes,
  retentionUnit,
}: {
  bucketIndex: number;
  bucketSizes: number[];
  retentionUnit: TimeUnit;
}) {
  const capitalizedRetentionUnit = capitalize(retentionUnit);
  const start = sum(bucketSizes.slice(0, bucketIndex));
  const end = start + bucketSizes[bucketIndex] - 1;

  return `${
    bucketIndex === 0
      ? `<${end + 1} ${pluralize(capitalizedRetentionUnit, end + 1)}`
      : `${
          bucketSizes[bucketIndex] === 1
            ? `${capitalizedRetentionUnit} ${start}`
            : `${pluralize(capitalizedRetentionUnit, true)} ${start}-${end}`
        }`
  }`;
}

export function getRetentionBucketStartAndEnd({
  bucketIndex,
  bucketSizes,
}: {
  bucketIndex: number;
  bucketSizes: number[];
}) {
  const start = sum(bucketSizes.slice(0, bucketIndex));
  const end = start + bucketSizes[bucketIndex] - 1;
  return {start, end};
}

export function getFrequencyTooltipSubtitle({
  cumulative,
  dateUnit,
  bucketIndex,
}: {
  cumulative: boolean;
  dateUnit: TimeUnit;
  bucketIndex: number;
}) {
  const count = bucketIndex + 2;
  return `${cumulative ? `Did frequency event at least` : `In minimum`} ${count} ${pluralize(
    getAddictionUnit(dateUnit),
    count,
  )} in a ${dateUnit}`;
}

export function getEventDisplayName(event: string): string {
  let eventName;
  if (event === SPECIAL_EVENTS.ANY_EVENT) {
    eventName = `Anything`;
  } else if (event.includes(`$custom_event`)) {
    const id = parseInt(event.substring(14));
    eventName = customEventsStore.value[id]?.name ?? `Deleted Custom Event`;
  } else {
    eventName = renameEvent(event);
  }
  return eventName;
}
